import React from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {ToggleWrapper, ToggleTab} from './Tabs.styles'

/**
 * TabToggle
 *
 * Segmented button group — each tab navigates to a route.
 *
 * Active detection rules (first match wins):
 *  1. pathname matches the tab's path exactly
 *  2. pathname starts with the tab's path  (sub-routes stay highlighted)
 *  3. No tab matches → fall back to the first tab (defaultIndex=0)
 *     This covers the brief moment during an index redirect before the
 *     URL has settled, so My Accounts is always highlighted by default.
 *
 * @param {Array<{ label: string, path: string }>} tabs
 * @param {number} [defaultIndex=0] - index of the tab to treat as default
 */
export function TabToggle({tabs, defaultIndex = 0}) {
  const navigate = useNavigate()
  const {pathname} = useLocation()

  // Also treat the root path as matching the default tab so that
  // / shows My Accounts as active before the
  // index redirect to /accounts/my-accounts fires.
  const isRootPath = pathname === '/'
  const activeIndex = isRootPath
    ? defaultIndex
    : tabs.findIndex(tab => pathname.startsWith(tab.path))
  // If no tab matches fall back to defaultIndex (covers /accounts before
  // the index redirect resolves).
  const resolvedActiveIndex = activeIndex === -1 ? defaultIndex : activeIndex

  return (
    <ToggleWrapper role="tablist" aria-label="Account view">
      {tabs.map((tab, idx) => (
        <ToggleTab
          key={tab.path}
          role="tab"
          aria-selected={idx === resolvedActiveIndex}
          isActive={idx === resolvedActiveIndex}
          onClick={() => navigate(tab.path)}
        >
          {tab.label}
        </ToggleTab>
      ))}
    </ToggleWrapper>
  )
}
